import { Component, OnInit, OnDestroy } from '@angular/core';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { ActivatedRoute, Params } from '@angular/router';
import { Observable, Subject, throwError } from 'rxjs';
import { tap, takeUntil, catchError } from 'rxjs/operators';

// INTERFACES
import {
  LocalStorageKeys,
  OrderReturnApiService,
  OptionInterface,
  IPaginatorEvent,
  IPagination,
  PaginationConfig,
} from '../../../shared';
import { OrderSalesInterface } from '../../interfaces/order-sales.interface';

import { OrderReturnInterface } from './../../interfaces/order-return.interface';

@Component({
  selector: 'lum-order-return',
  templateUrl: './order-return.component.html',
  styleUrls: ['./order-return.component.scss'],
})
export class OrderReturnComponent implements OnInit, OnDestroy {
  parentSalesOrderId :number = 0;

  currentParentSalesOrder$ :Observable<OrderSalesInterface>;
  orderReturns$ :Observable<OrderReturnInterface[]>;
  pagination$ :Observable<IPagination>;
  loading$ :Observable<boolean>;

  currentParentSalesOrder :OrderSalesInterface;
  orderReturns :OrderReturnInterface[] = [];

  queryParams :{ [param :string] :string | string[] };
  searchQuery :string = '';

  paginationConfig = PaginationConfig;

  private onDestroy$ = new Subject();

  constructor(
    private orderReturnService :OrderReturnApiService,
    private route :ActivatedRoute,
  ) {}

  ngOnInit() :void {
    this.initQueryParams();
    this.getDataFromService();
    this.getRouterParams().subscribe();
    this.getParentSalesOrder().subscribe();
    this.getOrderReturns().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  private initQueryParams() :void {
    this.queryParams = {
      limit: PaginationConfig.perPage.toString(),
      page: PaginationConfig.page.toString(),
      sortDirection: 'desc',
      sortField: 'created_at',
    };
  }

  private loadOrderReturns() :void {
    this.orderReturnService.getOrderReturnsByInvoiceId({
      params: {
        ...this.queryParams,
        searchQuery: this.searchQuery,
        parentId: this.parentSalesOrderId.toString(),
        relations: 'parentSalesOrder',
      },
    });
  }

  private loadParentSalesOrder(parentSalesOrderId :number) :void {
    this.orderReturnService.getParentSalesOrderById({
      parentSalesOrderId,
      params: {
        relations:
          'orderItems.rfq,orderItems.kit,orderItems.orderedUom,orderItems.baseUom',
      },
    });
  }

  private getDataFromService() :void {
    this.currentParentSalesOrder$ =
      this.orderReturnService.currentParentSalesOrder$;
    this.orderReturns$ = this.orderReturnService.orderReturns$;
    this.pagination$ = this.orderReturnService.pagination$;
    this.loading$ = this.orderReturnService.loading$;
  }

  private getOrderReturns() :Observable<OrderReturnInterface[]> {
    return this.orderReturns$.pipe(
      tap((orderReturns) => {
        if (orderReturns) {
          this.orderReturns = orderReturns;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getParentSalesOrder() :Observable<OrderSalesInterface> {
    return this.currentParentSalesOrder$.pipe(
      tap((currentParentSalesOrder) => {
        if (currentParentSalesOrder) {
          this.currentParentSalesOrder = currentParentSalesOrder;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  onSearchEvent(searchQuery :string) :void {
    this.searchQuery = searchQuery;
    this.loadOrderReturns();
  }

  public resetFilters() :void {
    this.initQueryParams();
    this.loadOrderReturns();
  }

  private getRouterParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const { parentSalesOrderId } = params;
        if (parentSalesOrderId) {
          this.parentSalesOrderId = parentSalesOrderId;
          this.loadParentSalesOrder(this.parentSalesOrderId);
          this.loadOrderReturns();
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.queryParams = {
      ...this.queryParams,
      limit: pagination.pageSize.toString(),
      page: (pagination.pageIndex + 1).toString(),
    };
    this.loadOrderReturns();
  }
}
